import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { EventForm } from '@/components/events/EventForm';
import { type EventFormValues } from '@/lib/schemas/event';
import { Card, CardContent, CardHeader, CardTitle as CardHeaderTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EventsPageShell } from './components/EventsPageShell';
import { readUpdateAudience, storeMarketingReport } from '@/lib/marketing-report';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

export default function EditEventPage() {
  const { eventId, communityId } = useParams<{ eventId: string; communityId: string }>();
  const navigate = useNavigate();

  const { data, error, isLoading } = useSWR(eventId ? `/api/events/${eventId}` : null, fetcher);
  const { data: activities, isLoading: activitiesLoading } = useSWR(
    eventId ? `/api/events/${eventId}/activities` : null,
    fetcher
  );

  if (isLoading || activitiesLoading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data?.event) {
    return (
      <div className="container mx-auto py-10 text-center">
        <h2 className="text-xl font-semibold mb-2">Event not found</h2>
        <Button variant="outline" onClick={() => navigate(`/${communityId}/events`)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Events
        </Button>
      </div>
    );
  }

  const { event } = data;
  const hasAttendanceActivity = Array.isArray(activities) && activities.length > 0;

  const initialData = {
    title: event.title,
    slug: event.slug || '',
    communityId: communityId || '',
    startDate: new Date(event.startDate).toISOString(),
    endDate: new Date(event.endDate).toISOString(),
    locationType: event.locationType,
    address: event.address || '',
    eventType: event.eventType || 'SOCIAL',
    virtualUrl: event.virtualUrl || '',
    googleMapLink: event.googleMapLink || '',
    privacy: event.privacy || 'PUBLIC',
    allowDuplicatePhone: event.allowDuplicatePhone ?? true,
    isRegistrationClosed: event.isRegistrationClosed || false,
    markAttendance: hasAttendanceActivity,
    banner: event.banner || '',
    checkoutBgImage: event.checkoutBgImage || '',
    description: event.description || '',
    tickets: event.tickets.map(
      (t: {
        id: string;
        name: string;
        type?: string;
        price?: number;
        quantity?: number | null;
        enabled?: boolean;
        formSchema?: unknown[];
        allowedAttendeeTypes?: string[];
      }) => ({
        id: t.id,
        name: t.name,
        type: (t.type as 'MEMBER_ONLY' | 'MEMBER_GUEST' | 'OPEN_FOR_ALL') || 'OPEN_FOR_ALL',
        price: t.price || 0,
        quantity: t.quantity ?? null,
        enabled: t.enabled !== false,
        formSchema: t.formSchema || [],
        allowedAttendeeTypes: t.allowedAttendeeTypes || ['GUEST'],
      })
    ),
  };

  const handleUpdateEvent = async (values: EventFormValues) => {
    try {
      const storedAudience = readUpdateAudience(event.id);
      const notifyAudience =
        storedAudience === 'REGISTERED' ||
        storedAudience === 'UNREGISTERED' ||
        storedAudience === 'ALL_MEMBERS'
          ? storedAudience
          : null;
      const { data: updated } = await axiosInstance.patch(`/api/events/${eventId}`, {
        ...values,
        ...(notifyAudience ? { notifyAudience } : {}),
      });
      if (updated?.marketing) {
        storeMarketingReport(event.id, {
          batchId: updated.marketing.batchId || 'pending',
          reason: updated.marketing.templateMissing
            ? 'No template is configured for this purpose.'
            : updated.marketing.reason,
          audience: updated.marketing.audience || '',
          eventTitle: updated.marketing.snapshot?.eventTitle || event.title,
          eventStartDate: updated.marketing.snapshot?.eventStartDate || '',
          eventLocation: updated.marketing.snapshot?.eventLocation || '',
          messageType: 'EVENT_UPDATE',
          sent: 0,
          failed: 0,
          latestAt: new Date().toISOString(),
          queued: updated.marketing.queued,
          templateMissing: updated.marketing.templateMissing,
        });
      }
      navigate(`/${communityId}/events/${values.slug || eventId}`);
    } catch (error) {
      console.error('Failed to update event:', error);
    }
  };

  return (
    <EventsPageShell>
      <Button
        variant="ghost"
        className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
        onClick={() => navigate(`/${communityId}/events/${event.id}`)}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Event
      </Button>

      {data?.event?.totalRegistrationsCount > 0 && (
        <AlertDialog defaultOpen={true}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-destructive">
                Existing Registrations!
              </AlertDialogTitle>
              <AlertDialogDescription>
                There are already <strong>{data.event.totalRegistrationsCount}</strong> active
                registrations for this event. Editing details or tickets might affect existing
                members. Do you still want to continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => navigate(`/${communityId}/events/${event.id}`)}>
                Go Back
              </AlertDialogCancel>
              <AlertDialogAction>Continue Editing</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <Card className="w-full border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl">
        <CardHeader>
          <CardHeaderTitle>Edit Event</CardHeaderTitle>
        </CardHeader>
        <CardContent>
          <EventForm
            initialValues={initialData}
            onSubmit={handleUpdateEvent}
            submitButtonText="Update Event"
            onCancel={() => navigate(`/${communityId}/events/${event.id}`)}
            markAttendanceLocked={hasAttendanceActivity}
          />
        </CardContent>
      </Card>
    </EventsPageShell>
  );
}
